import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { User, LogOut, LayoutDashboard, Ticket, BookOpen, BarChart3, Sun, Moon, Settings, Wifi, Users, Cog, ChevronDown, Eye, Menu, X, Shield } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { useRBAC } from "../contexts/RBACContext";
import NotificationBell from "./NotificationBell";
import { useState } from "react";

export default function Navbar({ user, logout, effectiveRole, previewRole, setPreviewRole, previewDealership, setPreviewDealership, previewDepartment, setPreviewDepartment }) {
  const { theme, toggleTheme } = useTheme();
  const { hasPermission } = useRBAC();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Use effectiveRole if provided (for preview mode), otherwise use user.role
  const displayRole = effectiveRole || user?.role;
  
  // Check if current user can preview roles using dynamic RBAC
  const canPreviewRoles = hasPermission('view_testing', user?.role);
  
  // Dynamic permission checks for navigation
  const canViewAnalytics = hasPermission('analytics_view', displayRole);
  const canViewSettings = hasPermission('settings_users', displayRole) || hasPermission('settings_connections', displayRole) || hasPermission('settings_site', displayRole);
  const canViewConnections = hasPermission('settings_connections', displayRole);
  const canViewSiteSettings = hasPermission('settings_site', displayRole);
  
  // Get available roles to preview based on user's role
  const getAvailablePreviewRoles = () => {
    if (user?.role === "super_admin") {
      return [
        { value: "", label: "My View (Super Admin)" },
        { value: "admin", label: "View as Admin" },
        { value: "site_admin", label: "View as Site Admin" },
        { value: "department_admin", label: "View as Department Admin" },
        { value: "end_user", label: "View as End User" }
      ];
    } else if (user?.role === "admin") {
      return [
        { value: "", label: "My View (Admin)" },
        { value: "site_admin", label: "View as Site Admin" },
        { value: "department_admin", label: "View as Department Admin" },
        { value: "end_user", label: "View as End User" }
      ];
    }
    return [];
  };
  
  return (
    <nav className="bg-white dark:bg-[#1E1E1E] border-b border-slate-200 dark:border-[#3D3D3D] shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo Section - Fixed Width */}
          <div className="flex items-center flex-shrink-0">
            <Link to="/dashboard" className="flex items-center">
              <img 
                src="/niello-logo.png" 
                alt="Think Niello" 
                className="h-12 w-auto"
              />
            </Link>
          </div>
          
          {/* Navigation Links - Hidden on mobile */}
          <div className="hidden lg:flex items-center space-x-1">
              <Link to="/dashboard">
                <Button variant="ghost" className="text-slate-600 dark:text-white hover:text-slate-800 dark:hover:text-white" data-testid="nav-dashboard">
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  Dashboard
                </Button>
              </Link>
              <Link to="/tickets">
                <Button variant="ghost" className="text-slate-600 dark:text-white hover:text-slate-800 dark:hover:text-white" data-testid="nav-tickets">
                  <Ticket className="w-4 h-4 mr-2" />
                  Tickets
                </Button>
              </Link>
              <Link to="/knowledge-base">
                <Button variant="ghost" className="text-slate-600 dark:text-white hover:text-slate-800 dark:hover:text-white" data-testid="nav-knowledge">
                  <BookOpen className="w-4 h-4 mr-2" />
                  Ticket History
                </Button>
              </Link>
              {canViewAnalytics && (
                <Link to="/admin">
                  <Button variant="ghost" className="text-slate-600 dark:text-white hover:text-slate-800 dark:hover:text-white" data-testid="nav-admin">
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Analytics
                  </Button>
                </Link>
              )}
              
              {/* Settings Dropdown - dynamic RBAC */}
              {canViewSettings && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="text-slate-600 dark:text-white hover:text-slate-800 dark:hover:text-white" data-testid="nav-settings">
                      <Settings className="w-4 h-4 mr-2" />
                      Settings
                      <ChevronDown className="w-4 h-4 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-48">
                    <DropdownMenuItem asChild>
                      <Link to="/users" className="w-full cursor-pointer">
                        <Users className="mr-2 h-4 w-4" />
                        User Admin
                      </Link>
                    </DropdownMenuItem>
                    {canViewConnections && (
                      <DropdownMenuItem asChild>
                        <Link to="/connections" className="w-full cursor-pointer">
                          <Wifi className="mr-2 h-4 w-4" />
                          Connections
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {canViewSiteSettings && (
                      <DropdownMenuItem asChild>
                        <Link to="/settings" className="w-full cursor-pointer">
                          <Cog className="mr-2 h-4 w-4" />
                          Site Management
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {(user?.role === "super_admin" || user?.role === "admin") && (
                      <DropdownMenuItem asChild>
                        <Link to="/audit-logs" className="w-full cursor-pointer" data-testid="nav-audit-logs">
                          <Shield className="mr-2 h-4 w-4" />
                          Audit Logs
                        </Link>
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
          </div>

          {/* User Menu - Always Visible */}
          <div className="flex items-center gap-1 sm:gap-2">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={toggleTheme}
              className="text-slate-600 dark:text-white hover:text-slate-800 dark:hover:text-white"
              title={theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
            >
              {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </Button>

            <NotificationBell user={user} />

            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center space-x-2" data-testid="user-menu-trigger">
                <div className="bg-blue-100 dark:bg-[#343434] p-2 rounded-full">
                  <User className="w-4 h-4 text-blue-600 dark:text-white" />
                </div>
                <span className="hidden sm:inline text-sm font-medium text-slate-700 dark:text-white">{user?.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">{user?.name}</p>
                  <p className="text-xs leading-none text-muted-foreground">{user?.email}</p>
                </div>
              </DropdownMenuLabel>
              
              {/* Role Preview Selector for Admin and Super Admin */}
              {canPreviewRoles && setPreviewRole && (
                <>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-2">
                    <div className="flex items-center gap-2 mb-2">
                      <Eye className="w-4 h-4 text-muted-foreground" />
                      <span className="text-xs font-medium text-muted-foreground">View Testing</span>
                    </div>
                    
                    {/* Role Preview */}
                    <div className="mb-2">
                      <span className="text-xs text-muted-foreground mb-1 block">Role</span>
                      <Select 
                        value={previewRole || "default"} 
                        onValueChange={(value) => setPreviewRole(value === "default" ? null : value)}
                      >
                        <SelectTrigger className="w-full h-8 text-xs">
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent>
                          {getAvailablePreviewRoles().map((role) => (
                            <SelectItem key={role.value || "default"} value={role.value || "default"} className="text-xs">
                              {role.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {previewRole && (
                      <p className="text-xs text-amber-600 mt-2">
                        Currently viewing as {previewRole.replace("_", " ")}
                      </p>
                    )}
                  </div>
                </>
              )}
              
              <DropdownMenuSeparator />
              
              {/* Mobile Navigation Links */}
              <div className="lg:hidden">
                <DropdownMenuItem asChild>
                  <Link to="/dashboard" className="w-full">
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    Dashboard
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/tickets" className="w-full">
                    <Ticket className="mr-2 h-4 w-4" />
                    Tickets
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/knowledge-base" className="w-full">
                    <BookOpen className="mr-2 h-4 w-4" />
                    Ticket History
                  </Link>
                </DropdownMenuItem>
                {canViewAnalytics && (
                  <DropdownMenuItem asChild>
                    <Link to="/admin" className="w-full">
                      <BarChart3 className="mr-2 h-4 w-4" />
                      Analytics
                    </Link>
                  </DropdownMenuItem>
                )}
                {canViewSettings && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs text-muted-foreground">Settings</DropdownMenuLabel>
                    <DropdownMenuItem asChild>
                      <Link to="/users" className="w-full">
                        <Users className="mr-2 h-4 w-4" />
                        User Admin
                      </Link>
                    </DropdownMenuItem>
                    {canViewConnections && (
                      <DropdownMenuItem asChild>
                        <Link to="/connections" className="w-full">
                          <Wifi className="mr-2 h-4 w-4" />
                          Connections
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {canViewSiteSettings && (
                      <DropdownMenuItem asChild>
                        <Link to="/settings" className="w-full">
                          <Cog className="mr-2 h-4 w-4" />
                          Site Management
                        </Link>
                      </DropdownMenuItem>
                    )}
                  </>
                )}
                <DropdownMenuSeparator />
              </div>
              
              <DropdownMenuItem onClick={logout}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden text-slate-600 dark:text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-[#1a1a1a] px-4 py-3 space-y-1" data-testid="mobile-nav-menu">
          <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
            <Button variant="ghost" className="w-full justify-start text-slate-600 dark:text-white">
              <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
            </Button>
          </Link>
          <Link to="/tickets" onClick={() => setMobileMenuOpen(false)}>
            <Button variant="ghost" className="w-full justify-start text-slate-600 dark:text-white">
              <Ticket className="w-4 h-4 mr-2" /> Tickets
            </Button>
          </Link>
          <Link to="/knowledge-base" onClick={() => setMobileMenuOpen(false)}>
            <Button variant="ghost" className="w-full justify-start text-slate-600 dark:text-white">
              <BookOpen className="w-4 h-4 mr-2" /> Ticket History
            </Button>
          </Link>
          {canViewAnalytics && (
            <Link to="/admin" onClick={() => setMobileMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start text-slate-600 dark:text-white">
                <BarChart3 className="w-4 h-4 mr-2" /> Analytics
              </Button>
            </Link>
          )}
          {canViewSettings && (
            <>
              <Link to="/users" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="ghost" className="w-full justify-start text-slate-600 dark:text-white">
                  <Users className="w-4 h-4 mr-2" /> User Admin
                </Button>
              </Link>
              {canViewConnections && (
                <Link to="/connections" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-slate-600 dark:text-white">
                    <Wifi className="w-4 h-4 mr-2" /> Connections
                  </Button>
                </Link>
              )}
              {canViewSiteSettings && (
                <Link to="/settings" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-slate-600 dark:text-white">
                    <Cog className="w-4 h-4 mr-2" /> Site Management
                  </Button>
                </Link>
              )}
              {(user?.role === "super_admin" || user?.role === "admin") && (
                <Link to="/audit-logs" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-slate-600 dark:text-white">
                    <Shield className="w-4 h-4 mr-2" /> Audit Logs
                  </Button>
                </Link>
              )}
            </>
          )}
        </div>
      )}
    </nav>
  );
}
